<template>
  <div class="language-switcher" @keydown="handleKeydown">
    <button
      type="button"
      class="language-switcher__trigger"
      :class="{ 'language-switcher__trigger--open': isOpen }"
      :aria-label="$t('language.label') + ': ' + currentOption.label"
      :title="$t('language.label')"
      aria-haspopup="listbox"
      :aria-expanded="String(isOpen)"
      @click="toggleMenu"
    >
      <svg class="language-switcher__icon" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M3 12h18M12 3c2.4 2.5 3.7 5.5 3.7 9S14.4 18.5 12 21M12 3c-2.4 2.5-3.7 5.5-3.7 9S9.6 18.5 12 21"></path>
      </svg>
    </button>
    <transition name="language-menu">
      <div v-if="isOpen" class="language-switcher__menu" role="listbox">
        <button
          v-for="option in localeOptions"
          :key="option.value"
          type="button"
          class="language-switcher__option"
          :class="{ 'language-switcher__option--selected': option.value === $i18n.locale }"
          role="option"
          :aria-selected="String(option.value === $i18n.locale)"
          @click="selectLocale(option.value)"
        >
          <span>{{ option.label }}</span>
          <span v-if="option.value === $i18n.locale" class="language-switcher__check" aria-hidden="true">✓</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import { setLocale } from '@/i18n';

export default {
  name: 'LanguageSwitcher',
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    currentOption() {
      const option = this.localeOptions.find(item => item.value === this.$i18n.locale);
      if (!option) {
        throw new Error(`Unsupported current locale: ${this.$i18n.locale}`);
      }
      return option;
    },
    localeOptions() {
      return [
        { value: 'zh-CN', label: this.$t('language.simplifiedChinese') },
        { value: 'zh-TW', label: this.$t('language.traditionalChinese') },
        { value: 'en-US', label: this.$t('language.english') }
      ];
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    toggleMenu() {
      this.isOpen = !this.isOpen;
    },
    closeMenu() {
      this.isOpen = false;
    },
    handleKeydown(event) {
      if (event.key !== 'Escape') {
        return;
      }
      event.stopPropagation();
      this.closeMenu();
    },
    selectLocale(locale) {
      setLocale(locale);
      this.closeMenu();
    },
    handleDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.closeMenu();
      }
    }
  }
};
</script>

<style scoped>
.language-switcher {
  position: fixed;
  z-index: 1000;
  top: max(12px, env(safe-area-inset-top));
  right: 12px;
  color: #5f432e;
  font-size: 12px;
}

.language-switcher__trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(121, 84, 45, 0.2);
  border-radius: 50%;
  outline: 0;
  background: rgba(255, 252, 245, 0.96);
  box-shadow: 0 5px 18px rgba(63, 42, 24, 0.1);
  color: inherit;
  font: inherit;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.language-switcher__trigger:hover,
.language-switcher__trigger:focus-visible,
.language-switcher__trigger--open {
  background: rgba(248, 235, 208, 0.98);
  box-shadow: 0 5px 18px rgba(63, 42, 24, 0.14), inset 0 0 0 1px rgba(154, 106, 41, 0.1);
  transform: translateY(-1px);
}

.language-switcher__icon {
  width: 18px;
  height: 18px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.language-switcher__menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 142px;
  padding: 6px;
  border: 1px solid rgba(121, 84, 45, 0.16);
  border-radius: 13px;
  background: rgba(255, 253, 248, 0.98);
  box-shadow: 0 12px 30px rgba(63, 42, 24, 0.16);
  backdrop-filter: blur(10px);
}

.language-switcher__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #5f432e;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 140ms ease, color 140ms ease;
}

.language-switcher__option:hover,
.language-switcher__option:focus-visible {
  outline: 0;
  background: rgba(181, 133, 69, 0.1);
}

.language-switcher__option--selected {
  background: linear-gradient(90deg, rgba(241, 216, 169, 0.62), rgba(248, 235, 208, 0.72));
  color: #8a591d;
  font-weight: 600;
}

.language-switcher__check {
  color: #b47a2a;
  font-size: 13px;
}

.language-menu-enter-active,
.language-menu-leave-active {
  transition: opacity 140ms ease, transform 140ms ease;
  transform-origin: top right;
}

.language-menu-enter,
.language-menu-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

@media (max-width: 374px) {
  .language-switcher__menu {
    width: 134px;
  }
}
</style>
